import styled from '@emotion/styled';
import { Delete, Upload } from '@mui/icons-material';
import { Box, IconButton, Typography, LinearProgress } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { GetPresignedUrl, UploadImageToS3 } from '../../services/service';

export const ImageUpload = ({
  imageUrl: initialImageUrl,
  onChange,
  onRemove,
  uploadLabel,
  fileId,
  type,
  disabled,
  setLoader,
  folderType,
}: {
  imageUrl?: string | null | undefined;
  onChange?: (url: string) => void;
  onRemove?: () => void;
  uploadLabel?: string;
  fileId: string;
  type: 'icon' | 'banner' | 'profile';
  disabled: boolean;
  setLoader?: Dispatch<SetStateAction<boolean>>;
  folderType: number;
}) => {
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [localImagePreview, setLocalImagePreview] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setImageUrl(initialImageUrl || '');
  }, [initialImageUrl]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (setLoader) {
      setLoader(true);
    }
    const file = e.target.files ? e.target.files[0] : null;
    if (file && ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      const reader = new FileReader();
      reader.onload = () => {
        const preview = reader.result as string;
        setLocalImagePreview(preview);
      };

      reader.readAsDataURL(file);

      setIsUploading(true);
      try {
        const FileName = file.name.split('.');
        const FileType = file.type.split('/');

        const presignedUrl = await GetPresignedUrl(
          `${FileName[0]}.${FileType[1]}`,
          file.type,
          folderType,
        );

        const uploadedImageUrl = await UploadImageToS3(file, presignedUrl);
        setImageUrl(uploadedImageUrl);
        onChange?.(uploadedImageUrl);
      } catch (error) {
        console.error('Error uploading image:', error);
      } finally {
        setIsUploading(false);
        setLocalImagePreview(null);
        if (setLoader) {
          setLoader(false);
        }
      }
    }
  };

  const handleRemoveFile = () => {
    setImageUrl('');
    onRemove?.();
  };

  const ImageBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isDashed',
  })<{ isDashed?: boolean }>(({ isDashed }) => ({
    border: isDashed ? '1px dashed #ddd' : '1px solid #ddd',
    borderRadius: 8,
    width: type === 'icon' ? '50%' : '100%',
    height: type === 'icon' ? 120 : 150,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: 16,
    cursor: 'pointer',
  }));

  const UploadIconBox = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    cursor: 'pointer',
    color: '#999',
  });

  const ImagePreview = styled('img')({
    width: type === 'icon' ? '50%' : '80%',
    height: type === 'icon' ? '80%' : '90%',
    objectFit: 'contain',
  });

  const LocalImagePreview = styled('img')({
    width: type === 'icon' ? '50%' : '80%',
    height: type === 'icon' ? '80%' : '90%',
    objectFit: 'contain',
    opacity: 0.5, // To show the preview with some transparency
  });

  if (isUploading && localImagePreview) {
    console.log('PREVIEW TIME');
  } else if (imageUrl) {
    console.log('IMAGE URL Time');
  } else {
    console.log('UPLOAD TIME');
  }
  return (
    <ImageBox isDashed={!imageUrl && !isUploading && !localImagePreview}>
      {isUploading ? (
        localImagePreview ? (
          <>
            <LocalImagePreview src={localImagePreview} alt="Uploading..." />
            <LinearProgress
              style={{ position: 'absolute', bottom: 0, width: '100%' }}
            />
          </>
        ) : (
          <Typography>Loading...</Typography>
        )
      ) : imageUrl ? (
        <ImagePreview src={imageUrl} alt="Image Preview" />
      ) : (
        <label htmlFor={fileId}>
          <UploadIconBox>
            <Upload fontSize="large" />
            <Typography variant="body2" style={{ marginLeft: 8 }}>
              {uploadLabel}
            </Typography>
          </UploadIconBox>
        </label>
      )}
      <input
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        id={fileId}
      />
      {!disabled && imageUrl && (
        <IconButton
          onClick={handleRemoveFile}
          style={{ position: 'absolute', top: 8, right: 8 }}
        >
          <Delete fontSize="small" />
        </IconButton>
      )}
    </ImageBox>
  );
};
